import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Dropdown from "react-bootstrap/Dropdown";
import { NavLink, useLocation } from "react-router-dom";
import { ArrowRepeat } from "react-bootstrap-icons";
import "bootstrap/dist/css/bootstrap.min.css";

import styles from "./Menu.module.css";
import history from "../../history";
import { fetchDeconnexion } from "../connexion/connexionSlice";
import { patchSelectedRestaurant, fetchSuperAdminRestaurants, listeRestaurantsSelector } from "../listeRestaurants/listeRestaurantsSlice";

import exitIcon from "./svg/exit.svg";
import graphIcon from "./svg/graph.svg";
import calendarIcon from "./svg/calendar.svg";
import reportIcon from "./svg/report.svg";
import loginPasswordIcon from "./svg/Login_password.svg";
import profileIcon from "./svg/profile.svg";

// Hook pour détecter le mode burger
function useIsBurger() {
  const [isBurger, setIsBurger] = useState(window.innerWidth <= 991);

  useEffect(() => {
    const handleResize = () => setIsBurger(window.innerWidth <= 991);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isBurger;
}

export const Menu = () => {
  const utilisateur = useSelector((state) => state.connexion.utilisateur);
  const { restaurants } = useSelector(listeRestaurantsSelector);
  const dispatch = useDispatch();

  const dashboard = useRef();
  const rapport = useRef();
  const cumul = useRef();
  const location = useLocation();

  const isBurger = useIsBurger();

  // Focus si sur certaines pages
  useEffect(() => {
    if (location.pathname === "/service/rapports") rapport.current?.focus();
    if (location.pathname === "/rapport/financier") rapport.current?.focus();
  }, [location.pathname]);

  // Charger les restaurants pour les superadmins si nécessaire
  useEffect(() => {
    if (utilisateur?.type === "superadmin" && restaurants.length === 0) {
      dispatch(fetchSuperAdminRestaurants());
    }
  }, [utilisateur, restaurants.length, dispatch]);

  // Sélection d’un restaurant
  const handleSelectRestaurant = (selectedValue) => {
    // Cas spécial pour l'administration
    if (selectedValue === "admin") {
      const payload = {
        id: utilisateur.id,
        restaurant: {
          restaurants_affichage: "admin",
          restaurants_nom: "Administration",
          restaurants_id: "admin",
        },
      };
      dispatch(patchSelectedRestaurant(payload));
      return;
    }

    // Pour les superadmins, chercher dans le store Redux, sinon dans le token
    const restaurantList = utilisateur.type === "superadmin" ? restaurants : utilisateur.restaurants_affichage_list;
    const selectedResto = restaurantList?.find(
      (item) => item.restaurants_affichage === selectedValue
    );
    if (selectedResto) {
      const payload = {
        id: utilisateur.id,
        restaurant: {
          restaurants_affichage: selectedResto.restaurants_affichage,
          restaurants_nom: selectedResto.restaurants_nom,
          restaurants_id: selectedResto.restaurants_id,
        },
      };
      dispatch(patchSelectedRestaurant(payload));
    }
  };

  /****************************************
   * 1) Version desktop : dropdown
   ****************************************/
  const liste_restaurant_dropdown = () => {
    const restaurantList = utilisateur.type === "superadmin" ? restaurants : utilisateur.restaurants_affichage_list;
    const hasMultiRestaurants = utilisateur.type === "superadmin" ? restaurants.length > 1 : utilisateur.multi_resto;
    
    if (hasMultiRestaurants) {
      // Séparer Administration des autres restaurants pour les superadmins
      let adminRestaurant = null;
      let otherRestaurants = restaurantList || [];
      
      if (utilisateur.type === "superadmin") {
        adminRestaurant = restaurantList?.find(resto => resto.restaurants_affichage === "admin");
        otherRestaurants = restaurantList?.filter(resto => resto.restaurants_affichage !== "admin") || [];
      }

      return (
        <Dropdown onSelect={handleSelectRestaurant} alignRight>
          <Dropdown.Toggle
            variant="secondary"
            id="dropdown-restaurant"
            className={`${styles.font} ${styles.dropdownToggle}`}
          >
            {utilisateur.rest_nom}
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.dropdownMenu}>
            {/* Pour les superadmins : Administration en premier */}
            {utilisateur.type === "superadmin" && adminRestaurant && (
              <>
                <Dropdown.Item
                  key={adminRestaurant.restaurants_id}
                  eventKey={adminRestaurant.restaurants_affichage}
                  style={{ color: "#fff", fontWeight: "900" }}
                >
                  {adminRestaurant.restaurants_nom}
                </Dropdown.Item>
                {otherRestaurants.length > 0 && <Dropdown.Divider />}
              </>
            )}
            
            {/* Conteneur avec scroll pour les autres restaurants */}
            <div className={styles.scrollableContent}>
              {otherRestaurants?.map((resto) => (
                <Dropdown.Item
                  key={resto.restaurants_id}
                  eventKey={resto.restaurants_affichage}
                  style={{ color: "#fff", fontWeight: "900" }}
                >
                  {resto.restaurants_nom}
                </Dropdown.Item>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      );
    } else {
      return <h5 className={styles.nom_resto}>{utilisateur.rest_nom}</h5>;
    }
  };

  /****************************************
   * 2) Version mobile : carousel horizontal avec transform: skew
   ****************************************/
  const chipRefs = useRef({});

  // Centrer le resto actif dès que ça change
  useEffect(() => {
    if (isBurger && utilisateur?.multi_resto) {
      const currentKey = utilisateur.rest_nom;
      if (chipRefs.current[currentKey]) {
        chipRefs.current[currentKey].scrollIntoView({
          inline: "center",
          behavior: "smooth",
        });
      }
    }
  }, [isBurger, utilisateur?.rest_nom, utilisateur?.multi_resto]);

  const liste_restaurant_mobile = () => {
    const restaurantList = utilisateur.type === "superadmin" ? restaurants : utilisateur.restaurants_affichage_list;
    const hasMultiRestaurants = utilisateur.type === "superadmin" ? restaurants.length > 1 : utilisateur.multi_resto;
    
    if (hasMultiRestaurants) {
      // Séparer Administration des autres restaurants pour les superadmins
      let adminRestaurant = null;
      let otherRestaurants = restaurantList || [];
      
      if (utilisateur.type === "superadmin") {
        adminRestaurant = restaurantList?.find(resto => resto.restaurants_affichage === "admin");
        otherRestaurants = restaurantList?.filter(resto => resto.restaurants_affichage !== "admin") || [];
      }

      return (
        <div className={styles.restaurantCarouselContainer}>
          <div className={styles.restaurantCarousel}>
            {/* Filler gauche pour centrer la liste */}
            <div className={styles.filler} />
            
            {/* Administration en premier pour les superadmins */}
            {utilisateur.type === "superadmin" && adminRestaurant && (
              <>
                <div
                  key={adminRestaurant.restaurants_id}
                  ref={(el) =>
                    (chipRefs.current[adminRestaurant.restaurants_affichage] = el)
                  }
                  onClick={() =>
                    handleSelectRestaurant(adminRestaurant.restaurants_affichage)
                  }
                  className={
                    adminRestaurant.restaurants_affichage === utilisateur.rest_nom
                      ? `${styles.restaurantChip} ${styles.restaurantChipActive}`
                      : styles.restaurantChip
                  }
                >
                  {/* Conteneur interne pour redresser le texte */}
                  <div className={styles.restaurantChipInner}>
                    {adminRestaurant.restaurants_nom}
                  </div>
                </div>
                
                {/* Séparateur visuel */}
                {otherRestaurants.length > 0 && (
                  <div className={styles.restaurantSeparator}>
                    <div className={styles.separatorLine}></div>
                  </div>
                )}
              </>
            )}
            
            {/* Autres restaurants */}
            {otherRestaurants?.map((resto) => {
              const isActive =
                resto.restaurants_affichage === utilisateur.rest_nom;
              return (
                <div
                  key={resto.restaurants_id}
                  ref={(el) =>
                    (chipRefs.current[resto.restaurants_affichage] = el)
                  }
                  onClick={() =>
                    handleSelectRestaurant(resto.restaurants_affichage)
                  }
                  className={
                    isActive
                      ? `${styles.restaurantChip} ${styles.restaurantChipActive}`
                      : styles.restaurantChip
                  }
                >
                  {/* Conteneur interne pour redresser le texte */}
                  <div className={styles.restaurantChipInner}>
                    {resto.restaurants_nom}
                  </div>
                </div>
              );
            })}

            {/* Filler droit optionnel */}
            <div className={styles.filler} />
          </div>
        </div>
      );
    } else {
      return <h5 className={styles.nom_resto}>{utilisateur.rest_nom}</h5>;
    }
  };

  const renderListeRestaurant = () => {
    if (location.pathname === "/listeRestaurants") {
      return <h5 className={styles.nom_resto}>Liste restaurant</h5>;
    } else {
      return isBurger
        ? liste_restaurant_mobile()
        : liste_restaurant_dropdown();
    }
  };

  /****************************************
   * Menu profil
   ****************************************/
  const profil_dropdown = () => (
    <Dropdown alignRight>
      <Dropdown.Toggle
        variant="secondary"
        id="dropdown-profil"
        className={`${styles.font} ${styles.dropdownToggle}`}
      >
        <span>
          <img className={styles.icon} src={profileIcon} alt="Profil" />
        </span>{" "}
        Profil
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.dropdownMenu}>
        <Dropdown.Header className={styles.dropdownHeader}>
          Configurations
        </Dropdown.Header>
        <Dropdown.Item as={NavLink} to="/ResetAlias" className={styles.font}>
          <span>
            <img className={styles.icon} src={profileIcon} alt="Alias" />
          </span>{" "}
          Alias
        </Dropdown.Item>
        <Dropdown.Item as={NavLink} to="/ResetPassword" className={styles.font}>
          <span>
            <img className={styles.icon} src={loginPasswordIcon} alt="Mot de passe" />
          </span>{" "}
          Mot de passe
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={() => dispatch(fetchDeconnexion())}
          className={styles.font}
        >
          <span>
            <img className={styles.icon} src={exitIcon} alt="Déconnexion" />
          </span>{" "}
          Déconnexion
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  const profil_mobile = () => (
    <div className={styles.profileList}>
      <h5 className={styles.profileTitle}>Profil</h5>
      <ul className={styles.profileItemList}>
        <li className={styles.profileItem}>
          <NavLink to="/ResetPassword" className={styles.font}>
            <span>
              <img className={styles.icon} src={loginPasswordIcon} alt="Mot de passe" />
            </span>{" "}
            Mot de passe
          </NavLink>
        </li>
        <li
          className={styles.profileItem}
          onClick={() => dispatch(fetchDeconnexion())}
        >
          <span>
            <img className={styles.icon} src={exitIcon} alt="Déconnexion" />
          </span>{" "}
          Déconnexion
        </li>
      </ul>
    </div>
  );

  /****************************************
   * SUPERADMIN - Navbar réduite seulement quand aucun restaurant n'est sélectionné
   ****************************************/
  if (utilisateur.type === "superadmin" && !utilisateur.rest_id_affichage) {
    return (
      <Navbar as="div" className={styles.menu} bg="dark" sticky="top" expand="lg" variant="light">
        <Navbar.Brand as="div" href="/dashboard" className={styles.font}>
          <img
            className={styles.logo_connexion}
            src={process.env.PUBLIC_URL + "/POKKYWEB_noir_L.png"}
            alt="Logo"
          />
        </Navbar.Brand>
        <Navbar.Toggle
          as="div"
          className={styles.bouton_mobile}
          aria-controls="basic-navbar-nav"
        />
        <Navbar.Collapse as="div" id="basic-navbar-nav">
          <Nav as="div" className="mr-auto">
            <Nav.Link
              as="div"
              onClick={() => dispatch(fetchDeconnexion())}
              className={styles.font}
            >
              Déconnexion
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    );
  }

  /****************************************
   * UTILISATEUR NORMAL – Version mobile
   ****************************************/
  if (isBurger) {
    return (
      <Navbar as="div" className={styles.menu} bg="dark" sticky="top" expand="lg" variant="light">
        <Navbar.Brand as="div" href="/dashboard" className={styles.font}>
          <img
            className={styles.logo_connexion}
            src={process.env.PUBLIC_URL + "/POKKYWEB_noir_L.png"}
            alt="Logo"
          />
        </Navbar.Brand>
        <Navbar.Toggle
          as="div"
          className={styles.bouton_mobile}
          aria-controls="basic-navbar-nav"
        />
        <Navbar.Collapse
          as="div"
          id="basic-navbar-nav"
          className={styles.navbarPosition}
        >
          <Nav as="div" className="mr-auto">
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {renderListeRestaurant()}
              <button
                className={styles.navigation}
                onClick={() => history.go(0)}
                title="Rafraîchir la page"
              >
                <ArrowRepeat color="white" size={30} />
              </button>
            </div>
            <Nav.Link as="div">
              <NavLink
                to="/dashboard"
                ref={dashboard}
                activeClassName={styles.active}
                className={styles.font}
              >
                Tableau de bord{" "}
                <span>
                  <img className={styles.icon} src={graphIcon} alt="Graph" />
                </span>
              </NavLink>
            </Nav.Link>
            <Nav.Link as="div">
              <NavLink
                to="/services"
                ref={rapport}
                activeClassName={styles.active}
                className={styles.font}
              >
                Rapport{" "}
                <span>
                  <img className={styles.icon} src={reportIcon} alt="Report" />
                </span>
              </NavLink>
            </Nav.Link>
            <Nav.Link as="div">
              <NavLink
                to="/cumul"
                ref={cumul}
                activeClassName={styles.active}
                className={styles.font}
              >
                Cumul{" "}
                <span>
                  <img className={styles.icon} src={calendarIcon} alt="Calendar" />
                </span>
              </NavLink>
            </Nav.Link>
          </Nav>
          <Nav as="div" className="ml-auto">
            {profil_mobile()}
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    );
  }

  /****************************************
   * UTILISATEUR NORMAL – Version desktop (> 991px)
   ****************************************/
  return (
    <Navbar as="div" className={styles.menu} bg="dark" sticky="top" expand="lg" variant="light">
      <Navbar.Brand as="div" href="/dashboard" className={styles.font}>
        <img
          className={styles.logo_connexion}
          src={process.env.PUBLIC_URL + "/POKKYWEB_noir_L.png"}
          alt="Logo"
        />
      </Navbar.Brand>
      <Navbar.Toggle
        as="div"
        className={styles.bouton_mobile}
        aria-controls="basic-navbar-nav"
      />
      <Navbar.Collapse as="div" id="basic-navbar-nav">
        <Nav as="div" className="mr-auto" style={{ alignItems: "center" }}>
          {renderListeRestaurant()}
          <button
            className={styles.navigation}
            onClick={() => history.go(0)}
            title="Rafraîchir la page"
          >
            <ArrowRepeat color="white" size={30} />
          </button>
          <Nav.Link as="div">
            <NavLink
              to="/dashboard"
              ref={dashboard}
              activeClassName={styles.active}
              className={styles.font}
            >
              Tableau de bord{" "}
              <span>
                <img className={styles.icon} src={graphIcon} alt="Graph" />
              </span>
            </NavLink>
          </Nav.Link>
          <Nav.Link as="div">
            <NavLink
              to="/services"
              ref={rapport}
              activeClassName={styles.active}
              className={styles.font}
            >
              Rapport{" "}
              <span>
                <img className={styles.icon} src={reportIcon} alt="Report" />
              </span>
            </NavLink>
          </Nav.Link>
          <Nav.Link as="div">
            <NavLink
              to="/cumul"
              ref={cumul}
              activeClassName={styles.active}
              className={styles.font}
            >
              Cumul{" "}
              <span>
                <img className={styles.icon} src={calendarIcon} alt="Calendar" />
              </span>
            </NavLink>
          </Nav.Link>
        </Nav>
        <Nav as="div" className="ml-auto">
          {profil_dropdown()}
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
};
